import { useEffect } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { MDXProvider } from "@mdx-js/react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { HelpLayout } from "./components/HelpLayout";
import { RelatedContent } from "./components/RelatedContent";
import { TableOfContents } from "./components/TableOfContents";
import { mdxComponents } from "./components/mdxComponents";
import { getAllGuides, getGuideBySlug } from "../../lib/help/loadGuides";
import { HELP_CATEGORIES } from "../../lib/help/types";

const CONTENT_ID = "help-guide-content";

export function GuidePage() {
  const { slug } = useParams<{ slug: string }>();
  const { hash } = useLocation();
  const guide = slug ? getGuideBySlug(slug) : undefined;

  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [slug, hash]);

  if (!guide) {
    return <Navigate to="/help/guides" replace />;
  }

  const { frontmatter, Component } = guide;
  const category = HELP_CATEGORIES.find((c) => c.id === frontmatter.category);

  const siblings = getAllGuides().filter((g) => g.frontmatter.category === frontmatter.category);
  const idx = siblings.findIndex((g) => g.frontmatter.slug === frontmatter.slug);
  const prev = idx > 0 ? siblings[idx - 1] : undefined;
  const next = idx < siblings.length - 1 ? siblings[idx + 1] : undefined;

  return (
    <HelpLayout
      crumbs={[
        { label: "Guides", to: "/help/guides" },
        { label: category?.title ?? "Guide", to: `/help/guides#${frontmatter.category}` },
        { label: frontmatter.title },
      ]}
    >
      <div className="grid gap-12 lg:grid-cols-[1fr_220px]">
        <article className="guide-article min-w-0">
          <header className="help-heading mb-8 border-b border-cream-300 pb-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-forest-700">
              {category?.title}
            </p>
            <h1 className="font-display text-4xl font-bold text-bark-900">{frontmatter.title}</h1>
            <p className="mt-3 text-lg text-bark-700">{frontmatter.description}</p>
          </header>

          <div id={CONTENT_ID} className="max-w-none">
            <MDXProvider components={mdxComponents}>
              <Component />
            </MDXProvider>
          </div>

          <RelatedContent guide={guide} />

          <nav className="mt-16 grid gap-4 border-t border-cream-300 pt-8 sm:grid-cols-2">
            {prev ? (
              <Link
                to={`/help/guides/${prev.frontmatter.slug}`}
                className="paper-card group flex items-center gap-3 rounded-xl border border-cream-300 bg-cream-50 p-4 transition-colors hover:border-forest-300"
              >
                <ArrowLeftIcon className="h-5 w-5 flex-shrink-0 text-forest-600" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wider text-bark-600">Previous</p>
                  <p className="truncate font-medium text-bark-900 group-hover:text-forest-700">
                    {prev.frontmatter.title}
                  </p>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {next ? (
              <Link
                to={`/help/guides/${next.frontmatter.slug}`}
                className="paper-card group flex items-center justify-end gap-3 rounded-xl border border-cream-300 bg-cream-50 p-4 text-right transition-colors hover:border-forest-300"
              >
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wider text-bark-600">Next</p>
                  <p className="truncate font-medium text-bark-900 group-hover:text-forest-700">
                    {next.frontmatter.title}
                  </p>
                </div>
                <ArrowRightIcon className="h-5 w-5 flex-shrink-0 text-forest-600" aria-hidden="true" />
              </Link>
            ) : (
              <div />
            )}
          </nav>
        </article>

        <aside className="hidden lg:block">
          <TableOfContents containerId={CONTENT_ID} />
        </aside>
      </div>
    </HelpLayout>
  );
}
